import React, { useState } from "react";
import { X, KeyRound, Eye, EyeOff, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const formatErr = (detail) => {
  if (!detail) return "Errore imprevisto";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
};

export const ChangePasswordModal = ({ onClose }) => {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const meetsMin = next.length >= 8;
  const matches = next && next === confirm;
  const canSubmit = current && meetsMin && matches && !busy;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    try {
      const { data } = await api.post("/auth/change-password", {
        current_password: current,
        new_password: next,
      });
      if (data.access_token) {
        localStorage.setItem("perla_token", data.access_token);
      }
      setDone(true);
      toast.success("Password aggiornata");
      setTimeout(onClose, 1500);
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="change-password-modal"
    >
      <div
        className="bg-[#FAFAF7] max-w-md w-full border-2 border-[#0A0A0A]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="bg-[#0A0A0A] text-[#FAFAF7] p-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <KeyRound size={18} className="text-[#FF5A1F]" />
            <h3 className="font-display text-2xl uppercase">Cambia password</h3>
          </div>
          <button onClick={onClose} data-testid="change-password-close">
            <X size={20} />
          </button>
        </div>

        {done ? (
          <div className="p-8 text-center" data-testid="change-password-success">
            <CheckCircle2 size={48} className="mx-auto text-[#FF5A1F]" />
            <div className="mt-4 font-display text-2xl uppercase text-[#0A0A0A]">Password aggiornata</div>
            <p className="mt-2 text-sm text-[#0A0A0A]/60">Sei ancora connesso con la nuova password.</p>
          </div>
        ) : (
          <form onSubmit={submit} className="p-6 space-y-4" data-testid="change-password-form">
            <Field
              label="Password attuale"
              value={current}
              onChange={setCurrent}
              type={show ? "text" : "password"}
              testId="cp-current"
              autoFocus
            />
            <Field
              label="Nuova password"
              value={next}
              onChange={setNext}
              type={show ? "text" : "password"}
              testId="cp-new"
            />
            <div className="text-xs">
              <div className={`flex items-center gap-2 ${meetsMin ? "text-[#0A0A0A]" : "text-[#0A0A0A]/40"}`}>
                {meetsMin ? "✓" : "○"} Almeno 8 caratteri
              </div>
            </div>
            <Field
              label="Conferma nuova password"
              value={confirm}
              onChange={setConfirm}
              type={show ? "text" : "password"}
              testId="cp-confirm"
            />
            {confirm && !matches && (
              <div className="text-xs text-red-600" data-testid="cp-mismatch">Le password non coincidono</div>
            )}

            <label className="flex items-center gap-2 text-sm text-[#0A0A0A]/70 cursor-pointer">
              <input
                type="checkbox"
                checked={show}
                onChange={(e) => setShow(e.target.checked)}
                data-testid="cp-show"
              />
              {show ? <EyeOff size={14} /> : <Eye size={14} />} Mostra password
            </label>

            <div className="flex justify-end gap-3 pt-4 border-t-2 border-[#0A0A0A]/10">
              <button type="button" onClick={onClose} className="btn-outline-dark" data-testid="cp-cancel">
                Annulla
              </button>
              <button
                type="submit"
                disabled={!canSubmit}
                className="btn-cta disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="cp-submit"
              >
                {busy ? "..." : "Aggiorna"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

const Field = ({ label, value, onChange, type = "text", testId, autoFocus }) => (
  <label className="block">
    <span className="text-xs uppercase tracking-widest font-bold text-[#0A0A0A]/60">{label}</span>
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      autoFocus={autoFocus}
      data-testid={testId}
      className="mt-2 block w-full border-2 border-[#0A0A0A] py-2 px-3 bg-white text-base focus:outline-none focus:border-[#FF5A1F]"
    />
  </label>
);
